import React, { forwardRef, useImperativeHandle } from 'react';
import styled from 'styled-components';
import { Button, Form, token } from '@bynder/design-system';
import { IconDelete } from '@bynder/icons';
import { useGradient } from './GradientContext';
import { NumberInputInlineSuffix } from '../NumberInputInlineSuffix';
import { GradientThumbnail } from 'packages/ds/GradientThumbnail/GradientThumbnail';
import useForceUpdate from 'packages/hooks/useForceUpdate';

const RowStyled = styled(Form.Row)`
    grid-template-columns: repeat(auto-fit, minmax(10px, auto));
    gap: ${token.spacing3};
`;

export type ThumbnailRef = {
    forceUpdate: () => void;
};

export type CommonGradientOptionsRef = {
    forceUpdate: () => void;
};

export function GradientOptions() {
    const { thumbnailRef, commonGradientOptionsRef } = useGradient();

    return (
        <RowStyled>
            <Thumbnail ref={thumbnailRef} />
            <LinearGradientOptions />
            <CommonGradientOptions ref={commonGradientOptionsRef} />
        </RowStyled>
    );
}

const Thumbnail = forwardRef<ThumbnailRef>(function Thumbnail(_props, ref) {
    const { state } = useGradient();
    const forceUpdate = useForceUpdate();

    useImperativeHandle(
        ref,
        () => ({
            forceUpdate,
        }),
        [forceUpdate],
    );

    return <GradientThumbnail {...state.gradient} />;
});

function LinearGradientOptions() {
    const { state, changeAngle } = useGradient();

    if (state.gradient.type !== 'linear') {
        return null;
    }

    const angle = state.gradient.angle ?? 0;

    return <NumberInputInlineSuffix value={angle} min={0} max={360} suffix="°" onChange={changeAngle} />;
}

const CommonGradientOptions = forwardRef<CommonGradientOptionsRef>(function CommonGradientOptions(_props, ref) {
    const { state, dispatch, updateSelectedStop, sliderRef } = useGradient();
    const forceUpdate = useForceUpdate();

    useImperativeHandle(
        ref,
        () => ({
            forceUpdate,
        }),
        [forceUpdate],
    );

    const selectedStop = state.gradient.stops[state.selected];

    const position = selectedStop.position;
    const hasMultipleStops = state.gradient.stops.length > 1;

    return (
        <>
            <NumberInputInlineSuffix
                value={position}
                min={0}
                max={100}
                suffix="%"
                onChange={(value) => {
                    updateSelectedStop({ position: value });
                    sliderRef.current?.forceUpdate();
                }}
            />

            <Button
                variant="clean"
                icon={<IconDelete />}
                aria-label="Delete stop"
                isDisabled={!hasMultipleStops}
                onClick={() => dispatch({ type: 'remove-selected-stop' })}
            />
        </>
    );
});
